import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useDispatch } from "react-redux";
import { uploadFinancialDocument } from "redux/actions";
import { ReportType, ReportYear } from "types/models/investorDeskMaster/InvestorDeskMasterApp";

interface FileEntry {
  title: string;
  document_title: string;
  file: File | null;
}

interface AddFileDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  investorDeskId?: number | null;
  reportType: ReportType | null;
  reportYear: ReportYear | null;
  quarter: { id: number; name: string } | null;
  company: { id: number; name: string } | null;
}

const AddFileDialog: React.FC<AddFileDialogProps> = ({
  open,
  onClose,
  onSuccess,
  investorDeskId,
  reportType,
  reportYear,
  quarter,
  company,
}) => {
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileList, setFileList] = useState<FileEntry[]>([
    { title: "", document_title: "", file: null },
  ]);

  const handleAddFileRow = () => {
    setFileList((prev) => [
      ...prev,
      { title: "", document_title: "", file: null },
    ]);
  };

  const handleRemoveFileRow = (index: number) => {
    if (fileList.length === 1) return;
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (
    index: number,
    field: "title" | "document_title",
    value: string
  ) => {
    setFileList((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleFileSelect = (index: number, file: File | null) => {
    if (!file) return;
    const fileNameWithoutExt =
      file.name.lastIndexOf(".") !== -1
        ? file.name.substring(0, file.name.lastIndexOf("."))
        : file.name;

    setFileList((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              file,
              title: item.title || fileNameWithoutExt,
              document_title: item.document_title || file.name,
            }
          : item
      )
    );
  };

  const handleUploadAll = async () => {
    // Validate
    for (let i = 0; i < fileList.length; i++) {
      const item = fileList[i];
      if (!item.title.trim()) {
        setErrorMessage(`Please enter a title for item #${i + 1}`);
        return;
      }
      if (!item.document_title.trim()) {
        setErrorMessage(`Please enter a document title for item #${i + 1}`);
        return;
      }
      if (!item.file) {
        setErrorMessage(`Please select a file to upload for item #${i + 1}`);
        return;
      }
    }

    if (!reportType || !reportYear) {
      setErrorMessage("Report type and year are required");
      return;
    }

    setIsUploading(true);
    setErrorMessage("");

    try {
      for (const item of fileList) {
        const formData = new FormData();
        formData.append("report_type_id", String(reportType.id));
        const yearVal = reportYear.year || reportYear.report_year || "";
        formData.append("report_year", String(yearVal));
        if (investorDeskId) {
          formData.append("investor_desk_id", String(investorDeskId));
        }
        formData.append("title", item.title.trim());
        formData.append("document_title", item.document_title.trim());
        formData.append("status", "active");
        if (item.file) {
          formData.append("file", item.file);
          if (item.file.type) {
            formData.append("mime_type", item.file.type);
          }
        }
        if (quarter) {
          formData.append("quarter", String(quarter.id));
        }
        if (company) {
          formData.append("subsidiary", String(company.id));
        }

        await dispatch(uploadFinancialDocument(formData) as any);
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      setErrorMessage(err?.message || "Failed to upload one or more files");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFileList([{ title: "", document_title: "", file: null }]);
    setErrorMessage("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 20,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        Add Files
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Context metadata badge bar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 3,
            p: 2,
            bgcolor: "#f9fafb",
            borderRadius: 2,
            border: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <strong>Report Type:</strong> {reportType?.name || "N/A"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            • <strong>Year:</strong> {reportYear?.report_year || reportYear?.year || "N/A"}
          </Typography>
          {quarter && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              • <strong>Quarter:</strong> {quarter.name}
            </Typography>
          )}
          {company && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              • <strong>Company:</strong> {company.name}
            </Typography>
          )}
        </Box>

        {errorMessage && (
          <Box
            sx={{
              p: 1.5,
              mb: 2.5,
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 2,
              color: "#dc2626",
              fontSize: 14,
            }}
          >
            {errorMessage}
          </Box>
        )}

        {/* Dynamic File Rows */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {fileList.map((entry, index) => (
            <Box
              key={index}
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: "1px solid #e5e7eb",
                bgcolor: "#ffffff",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#4b5563" }}>
                  File #{index + 1}
                </Typography>
                {fileList.length > 1 && (
                  <Tooltip title="Remove file">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFileRow(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  label="Title"
                  size="small"
                  fullWidth
                  placeholder="Enter title"
                  value={entry.title}
                  onChange={(e) =>
                    handleInputChange(index, "title", e.target.value)
                  }
                />
                <TextField
                  label="Document Title"
                  size="small"
                  fullWidth
                  placeholder="Enter document title"
                  value={entry.document_title}
                  onChange={(e) =>
                    handleInputChange(index, "document_title", e.target.value)
                  }
                />
              </Box>

              {/* File picker button */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <input
                  type="file"
                  id={`file-picker-${index}`}
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.csv,.xlsx,.xls,.mp3,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleFileSelect(index, e.target.files?.[0] || null)
                  }
                />
                <label htmlFor={`file-picker-${index}`}>
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    sx={{ textTransform: "none", borderRadius: 1.5 }}
                  >
                    Upload File
                  </Button>
                </label>
                {entry.file && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#111827",
                      fontWeight: 500,
                      maxWidth: 320,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {entry.file.name}{" "}
                    <span style={{ color: "#6b7280", fontWeight: 400 }}>
                      ({entry.file.type || "file"})
                    </span>
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Add more button */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddFileRow}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Add Another File
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Button onClick={handleClose} disabled={isUploading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUploadAll}
          disabled={isUploading}
          startIcon={
            isUploading ? <CircularProgress size={18} color="inherit" /> : null
          }
          sx={{ textTransform: "none", px: 3, fontWeight: 600, borderRadius: 2 }}
        >
          {isUploading ? "Uploading..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFileDialog;
